import React, { useCallback } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import {
  ORDER_EXCHANGE_TYPE,
  ORDER_PURCHASE_TYPE,
  ORDER_REPAIR_TYPE,
  PURCHASE_ORDER_STATUSES,
  REPAIR_ORDER_STATUSES
} from "constants/orders";
import { useGetOrdersQuery } from "services/orders";
import type { Patient } from "services/types/patients.type";
import { formatBackendDateOnly } from "utils/common";
import EnumDataSource from "utils/enum-data-source.util";

import TextCaption from "./text-caption.component";

interface PatientRecentOrdersProps {
  patient: Patient;
}

const ORDERS_QUERY =
  "{id,status,created_at,type,clinic_order_number,supplier_order_number,type_display,clinic{id,name}}";

export const PatientRecentOrders: React.FC<PatientRecentOrdersProps> = ({
  patient
}) => {
  const theme = useTheme();
  const { data: orders = [], isLoading } = useGetOrdersQuery({
    limit: 2,
    patient: Number(patient.id),
    query: ORDERS_QUERY
  });

  const getOrderStatus = useCallback((type: number) => {
    if (type) {
      if (type === ORDER_PURCHASE_TYPE) {
        return PURCHASE_ORDER_STATUSES;
      } else if (type === ORDER_REPAIR_TYPE) {
        return REPAIR_ORDER_STATUSES;
      } else if (type === ORDER_EXCHANGE_TYPE) {
        return PURCHASE_ORDER_STATUSES;
      }
    }
    return new EnumDataSource({});
  }, []);

  const noOrders = !isLoading && orders.length === 0;

  return (
    <Stack gap={1} sx={{ background: misc.mutedPurple }}>
      <Stack gap={1} flexDirection="row">
        <Typography variant="body3" fontWeight="700" pb={0.5}>
          Recent Orders
        </Typography>
        {noOrders && <Typography variant="body3">No Recent Orders</Typography>}
      </Stack>
      {!noOrders && (
        <Stack gap={2}>
          {orders?.map((order) => (
            <Grid
              container
              xs={12}
              p={1.5}
              sx={{
                border: `1px solid ${neutral[100]}`,
                borderRadius: "8px",
                lineHeight: 1.6,
                background: neutral[50],
                boxShadow: theme.shadows[3]
              }}
            >
              <Grid xs={5.5} display="flex" alignItems="center" pb={1}>
                <Typography
                  variant="body2"
                  component="div"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  Type: {order.type_display}
                </Typography>
              </Grid>
              <Grid xs={5.5} pb={1}>
                Order #{order.clinic_order_number}
              </Grid>
              <Grid xs={12}>
                <TextCaption>Created Date:</TextCaption>{" "}
                {order?.created_at
                  ? formatBackendDateOnly(
                      String(order?.created_at),
                      "MM/dd/yyyy"
                    )
                  : "-"}
              </Grid>
              <Grid xs={12}>
                <TextCaption>Clinic:</TextCaption> {order?.clinic?.name}
              </Grid>
              <Grid xs={12}>
                <TextCaption>Status</TextCaption>:{" "}
                {getOrderStatus(order.type).getFromCode(order.status)}
              </Grid>
            </Grid>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
